import {useCallback} from "react"
import {
    createPatternCanvas,
    createTransformMatrix,
} from "../utils/createPatternCanvasUtils"
import {CONSTANTS} from "../constants/constants"
import type {Position, Dimensions, CropParams} from "../types/types"

interface TileRenderParams {
    mainCtx: CanvasRenderingContext2D
    img: HTMLImageElement | HTMLVideoElement
    position: Position
    dimensions: Dimensions
    cropParams: {
        cropX: number
        cropY: number
        scaledCropWidth: number
        scaledCropHeight: number
    }
    cropDimensions: Dimensions
    zoom: number
    setCropParams: (params: CropParams) => void
}

export const useTileMode = () => {
    const renderTile = useCallback((params: TileRenderParams) => {
        const {
            mainCtx,
            img,
            position,
            dimensions,
            cropParams,
            cropDimensions,
            zoom,
            setCropParams,
        } = params

        const {cropX, cropY, scaledCropWidth, scaledCropHeight} = cropParams
        const zoomFactor = zoom / 100
        const sourceWidth =
            img instanceof HTMLVideoElement ? img.videoWidth : img.width
        const sourceHeight =
            img instanceof HTMLVideoElement ? img.videoHeight : img.height
        if (sourceWidth === 0 || sourceHeight === 0) {
            console.warn("Source has zero dimensions:", {
                isVideo: img instanceof HTMLVideoElement,
                sourceWidth,
                sourceHeight,
                readyState:
                    img instanceof HTMLVideoElement ? img.readyState : "N/A",
            })
            return
        }

        const baseTileSize = Math.min(scaledCropWidth, scaledCropHeight)
        const aspectRatio = sourceWidth / sourceHeight

        let tileWidth, tileHeight
        if (aspectRatio > 1) {
            tileWidth = baseTileSize
            tileHeight = baseTileSize / aspectRatio
        } else {
            tileHeight = baseTileSize
            tileWidth = baseTileSize * aspectRatio
        }

        tileWidth *= zoomFactor
        tileHeight *= zoomFactor

        const mainPattern = createPatternCanvas({
            img,
            width: tileWidth,
            height: tileHeight,
            ctx: mainCtx,
        })

        if (!mainPattern) return

        const matrix = createTransformMatrix({
            x: cropX + position.x,
            y: cropY + position.y,
        })
        mainPattern.setTransform(matrix)

        mainCtx.globalAlpha = 0.5
        mainCtx.fillStyle = mainPattern
        mainCtx.fillRect(
            0,
            0,
            CONSTANTS.CONTAINER_SIZE,
            CONSTANTS.CONTAINER_SIZE
        )

        mainCtx.globalAlpha = 1
        mainCtx.save()
        mainCtx.beginPath()
        mainCtx.rect(cropX, cropY, scaledCropWidth, scaledCropHeight)
        mainCtx.clip()
        mainCtx.fillStyle = mainPattern
        mainCtx.fillRect(
            0,
            0,
            CONSTANTS.CONTAINER_SIZE,
            CONSTANTS.CONTAINER_SIZE
        )
        mainCtx.restore()

        mainCtx.strokeStyle = "black"
        mainCtx.lineWidth = 2
        mainCtx.strokeRect(cropX, cropY, scaledCropWidth, scaledCropHeight)

        const scale = dimensions.width / sourceWidth

        const sourceX = (cropX - position.x) / scale
        const sourceY = (cropY - position.y) / scale
        const sourceCropWidth = scaledCropWidth / scale
        const sourceCropHeight = scaledCropHeight / scale

        const baseTileSizeInSource = Math.min(sourceCropWidth, sourceCropHeight)
        let sourceTileWidth, sourceTileHeight

        if (aspectRatio > 1) {
            sourceTileWidth = baseTileSizeInSource
            sourceTileHeight = baseTileSizeInSource / aspectRatio
        } else {
            sourceTileHeight = baseTileSizeInSource
            sourceTileWidth = baseTileSizeInSource * aspectRatio
        }

        const calculatedParams = {
            offsetX: sourceX / sourceWidth,
            offsetY: sourceY / sourceHeight,
            repeatX: (sourceCropWidth / sourceTileWidth) * (100 / zoom),
            repeatY: (sourceCropHeight / sourceTileHeight) * (100 / zoom),
            cropWidth: cropDimensions.width,
            cropHeight: cropDimensions.height,
            sourceX,
            sourceY,
            sourceCropWidth,
            sourceCropHeight,
            sourceTileWidth,
            sourceTileHeight,
            sourceWidth,
            sourceHeight,
            zoom,
            mode: "tile" as const,
        }

        setCropParams(calculatedParams)
    }, [])

    return {renderTile}
}
